/**
 * The AI players (real players' likenesses, src/engine/personas.ts): their pictures, and a picker to choose
 * who you play against — in a solo game (up to 4) and for an AI seat at a multiplayer table (1). Seats you
 * don't choose are filled at random.
 *
 * Pictures: a player's photo when there is one (PHOTOS — add `assets/players/<id>.jpg` and list it here),
 * otherwise a placeholder silhouette with their initial.
 */
import { PERSONAS, persona, type Persona } from "../engine/personas.ts";

/** Persona id → photo. */
export const PHOTOS: Record<string, string> = {
  xris: "assets/Chris.jpg", chris: "assets/Chris.jpg", steve: "assets/Steve.jpg", rhett: "assets/Rhett.jpg", susanne: "assets/Susanne.jpg",
};

const esc = (s: string) => s.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
const hue = (s: string) => [...s].reduce((h, c) => (h * 31 + c.charCodeAt(0)) >>> 0, 7) % 360;

/** A persona's picture as HTML (a round photo, or the placeholder), `size` px. */
export function personaPic(p: Persona, size = 56): string {
  const photo = PHOTOS[p.id];
  if (photo) return `<img class="op-pic" src="${photo}" alt="" width="${size}" height="${size}" style="width:${size}px;height:${size}px">`;
  const h = hue(p.name);
  return `<svg class="op-pic" viewBox="0 0 64 64" width="${size}" height="${size}" aria-hidden="true" style="width:${size}px;height:${size}px">
    <circle cx="32" cy="32" r="32" fill="hsl(${h} 32% 24%)"/>
    <circle cx="32" cy="25" r="12" fill="hsl(${h} 30% 52%)"/>
    <path d="M10 58c3-13 12-19 22-19s19 6 22 19" fill="hsl(${h} 30% 52%)"/>
    <text x="32" y="30" text-anchor="middle" font-size="13" font-weight="700" fill="hsl(${h} 35% 18%)" font-family="system-ui,sans-serif">${esc(p.name.slice(0, 1))}</text></svg>`;
}
/** The picture for a seat by persona id (undefined: not one of the AI players). */
export const personaPicById = (id: string | null | undefined, size = 56): string | undefined => { const p = persona(id); return p ? personaPic(p, size) : undefined; };

const STYLE = `
.op-back { position: fixed; inset: 0; z-index: 400; background: rgba(5,6,10,.78); display: grid; place-items: center; padding: 16px; }
.op-panel { width: min(980px, 100%); max-height: calc(100vh - 32px); display: flex; flex-direction: column; gap: 12px; padding: 18px 20px;
  background: #15161f; border: 1px solid rgba(226,180,92,.45); border-radius: 16px; box-shadow: 0 30px 80px rgba(0,0,0,.7); color: #eee; }
.op-head { display: flex; align-items: baseline; justify-content: space-between; gap: 12px; flex-wrap: wrap; }
.op-head h2 { margin: 0; font-family: var(--font-heading, serif); color: var(--ui-gold, #e2b45c); font-size: 24px; }
.op-sub { margin: 0; color: #aab; font-size: 14px; }
.op-grid { overflow: auto; display: grid; grid-template-columns: repeat(auto-fill, minmax(150px, 1fr)); gap: 10px; padding: 2px; }
.op-card { display: flex; flex-direction: column; align-items: center; gap: 4px; padding: 10px 8px; border-radius: 12px; cursor: pointer; text-align: center;
  background: rgba(255,255,255,.04); border: 2px solid rgba(255,255,255,.1); color: inherit; font: inherit; }
.op-card:hover:not(:disabled) { border-color: rgba(226,180,92,.6); }
.op-card[aria-pressed="true"] { border-color: var(--ui-gold, #e2b45c); background: rgba(226,180,92,.14); }
.op-card:disabled { opacity: .35; cursor: not-allowed; }
.op-card b { font-size: 16px; }
.op-style { font-size: 12px; color: #c9c3b3; line-height: 1.25; }
.op-rec { font-size: 11px; color: #8d8fa0; }
.op-pic { border-radius: 50%; object-fit: cover; display: block; flex: none; }
.op-foot { display: flex; align-items: center; justify-content: space-between; gap: 10px; flex-wrap: wrap; }
.op-count { color: #c9c3b3; font-size: 14px; }
.op-btns { display: flex; gap: 8px; flex-wrap: wrap; }
@media (max-width: 520px) { .op-grid { grid-template-columns: repeat(2, 1fr); } }
`;
function addStyle(): void {
  if (document.getElementById("op-style")) return;
  const s = document.createElement("style"); s.id = "op-style"; s.textContent = STYLE; document.head.appendChild(s);
}

/**
 * Choose AI players. `max` = how many may be chosen; `exclude` = names already at the table (shown greyed);
 * resolves with the chosen ids (empty = all random), or null if cancelled.
 */
export function pickOpponents(opts: { max: number; chosen?: string[]; exclude?: string[]; title?: string }): Promise<string[] | null> {
  addStyle();
  const norm = (s: string) => s.toLowerCase().replace(/[^a-z]/g, "");
  const out = new Set((opts.chosen ?? []).filter((id) => persona(id)));
  const blocked = new Set((opts.exclude ?? []).map(norm));
  return new Promise((resolve) => {
    const back = document.createElement("div");
    back.className = "op-back";
    back.innerHTML = `<div class="op-panel" role="dialog" aria-modal="true" aria-labelledby="op-title">
      <div class="op-head"><h2 id="op-title">${esc(opts.title ?? "Choose your opponents")}</h2>
        <p class="op-sub">${opts.max > 1 ? `Pick up to ${opts.max}. Any seat you leave is filled at random.` : "Pick one, or let the table pick at random."}</p></div>
      <div class="op-grid">${PERSONAS.map((p) => `<button type="button" class="op-card" data-id="${p.id}" aria-pressed="${out.has(p.id)}" ${blocked.has(norm(p.name)) ? "disabled title=\"Already at this table\"" : ""}>
        ${personaPic(p, 64)}<b>${esc(p.name)}</b><span class="op-style">${esc(p.style)}</span>
        <span class="op-rec">${p.record.games} games · ${p.record.wins} wins</span></button>`).join("")}</div>
      <div class="op-foot"><span class="op-count" aria-live="polite"></span>
        <span class="op-btns"><button type="button" class="ui-btn-ghost" data-act="cancel">Cancel</button>
        <button type="button" class="ui-btn-ghost" data-act="random">${opts.max > 1 ? "All random" : "Random"}</button>
        <button type="button" class="ui-btn" data-act="done">Done</button></span></div></div>`;
    const count = back.querySelector<HTMLElement>(".op-count")!;
    const sync = () => {
      for (const b of back.querySelectorAll<HTMLButtonElement>(".op-card")) b.setAttribute("aria-pressed", String(out.has(b.dataset.id!)));
      count.textContent = out.size ? `${out.size} chosen${opts.max > 1 && out.size < opts.max ? ` · ${opts.max - out.size} random` : ""}` : opts.max > 1 ? "All random" : "Random";
    };
    const finish = (v: string[] | null) => { back.remove(); document.removeEventListener("keydown", key); resolve(v); };
    const key = (e: KeyboardEvent) => { if (e.key === "Escape") finish(null); };
    back.addEventListener("click", (e) => {
      const t = e.target as HTMLElement;
      if (t === back) return finish(null);
      const card = t.closest<HTMLButtonElement>(".op-card");
      if (card && !card.disabled) {
        const id = card.dataset.id!;
        if (out.has(id)) out.delete(id);
        else { if (opts.max === 1) out.clear(); if (out.size < opts.max) out.add(id); }
        sync();
        if (opts.max === 1 && out.size) finish([...out]);       // one seat: picking is choosing
        return;
      }
      const act = t.closest<HTMLElement>("[data-act]")?.dataset.act;
      if (act === "cancel") finish(null);
      else if (act === "random") finish([]);
      else if (act === "done") finish([...out]);
    });
    document.addEventListener("keydown", key);
    document.body.appendChild(back);
    sync();
    back.querySelector<HTMLElement>(".op-card:not(:disabled)")?.focus();
  });
}
